import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Shield } from "lucide-react";
import { useCreateContact, useContactTags, useOrganizationContacts } from "@/hooks/useContacts";
import { Badge } from "@/components/ui/badge";
import type { Contact, ContactTag } from "@/hooks/useContacts";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "cellar_manager", label: "Manager" },
  { value: "cellar_staff", label: "Staff" },
  { value: "client_viewer", label: "Client Viewer" },
];

interface CreateContactDialogProps {
  /** Controlled mode (no trigger button): used when another picker opens the dialog. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Values the form starts with each time it opens. */
  defaults?: {
    type?: "person" | "organization";
    company?: string;
    tags?: string[];
  };
  onCreated?: (contact: Contact) => void;
}

export function CreateContactDialog({
  open: controlledOpen,
  onOpenChange,
  defaults,
  onCreated,
}: CreateContactDialogProps = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  // Form state
  const [type, setType] = useState<"person" | "organization">("person");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneExt, setPhoneExt] = useState("");
  const [phoneMobile, setPhoneMobile] = useState("");
  const [website, setWebsite] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [visibleTo, setVisibleTo] = useState<string[]>([]);
  const [organizationId, setOrganizationId] = useState<string>("");

  const createContact = useCreateContact();
  const { data: tags = [] } = useContactTags();
  const { data: orgContacts = [] } = useOrganizationContacts();

  const resetForm = () => {
    setType("person");
    setFirstName("");
    setLastName("");
    setCompany("");
    setRole("");
    setEmail("");
    setPhone("");
    setPhoneExt("");
    setPhoneMobile("");
    setWebsite("");
    setAddress("");
    setNotes("");
    setSelectedTags([]);
    setVisibleTo([]);
    setOrganizationId("");
  };

  // Apply defaults each time the dialog opens
  useEffect(() => {
    if (!open || !defaults) return;
    resetForm();
    if (defaults.type) setType(defaults.type);
    if (defaults.company) setCompany(defaults.company);
    if (defaults.tags) setSelectedTags(defaults.tags);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // React bubbles submit through the portal: keep it from submitting a parent form
    e.stopPropagation();
    createContact.mutate(
      {
        type,
        organization_id: organizationId || null,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        company: company || undefined,
        role: role || undefined,
        email: email || undefined,
        phone: phone || undefined,
        phone_ext: phoneExt || undefined,
        phone_mobile: phoneMobile || undefined,
        website: website || undefined,
        address: address || undefined,
        tags: selectedTags,
        visible_to: visibleTo.length > 0 ? visibleTo : null,
        notes: notes || undefined,
      },
      {
        onSuccess: (contact) => {
          resetForm();
          setOpen(false);
          onCreated?.(contact);
        },
      }
    );
  };

  const toggleTag = (slug: string) => {
    setSelectedTags((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const toggleVisibleTo = (roleValue: string) => {
    setVisibleTo((prev) =>
      prev.includes(roleValue)
        ? prev.filter((r) => r !== roleValue)
        : [...prev, roleValue]
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Contact
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Contact</DialogTitle>
          <DialogDescription>
            Add a person or organization to your contact directory.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type selector */}
          <div className="space-y-2">
            <Label>Type</Label>
            <Select
              value={type}
              onValueChange={(v) => setType(v as "person" | "organization")}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="person">Person</SelectItem>
                <SelectItem value="organization">Organization</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Name fields */}
          {type === "person" ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Jane"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Smith"
                />
              </div>
            </div>
          ) : null}

          {/* Company */}
          <div className="space-y-2">
            <Label htmlFor="company">
              {type === "organization" ? "Organization Name" : "Company"}
            </Label>
            <Input
              id="company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder={
                type === "organization" ? "Acme Corp" : "Company name"
              }
              required={type === "organization"}
            />
          </div>

          {/* Parent org (person only) */}
          {type === "person" && orgContacts.length > 0 && (
            <div className="space-y-2">
              <Label>Organization</Label>
              <Select
                value={organizationId || "__none__"}
                onValueChange={(v) => setOrganizationId(v === "__none__" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Link to an organization..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {orgContacts.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.company}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Role / Title */}
          <div className="space-y-2">
            <Label htmlFor="role">
              {type === "person" ? "Title / Role" : "Type / Category"}
            </Label>
            <Input
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder={
                type === "person" ? "Sales Manager" : "Equipment supplier"
              }
            />
          </div>

          {/* Contact info */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phoneExt">Ext</Label>
              <Input
                id="phoneExt"
                value={phoneExt}
                onChange={(e) => setPhoneExt(e.target.value)}
                placeholder="123"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phoneMobile">Mobile</Label>
            <Input
              id="phoneMobile"
              value={phoneMobile}
              onChange={(e) => setPhoneMobile(e.target.value)}
              placeholder="(555) 987-6543"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Textarea
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Main St, Napa, CA 94558"
              rows={2}
            />
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag: ContactTag) => {
                  const isSelected = selectedTags.includes(tag.tag);
                  return (
                    <Badge
                      key={tag.id}
                      variant={isSelected ? "default" : "outline"}
                      className="cursor-pointer select-none"
                      style={
                        isSelected && tag.color
                          ? { backgroundColor: tag.color, borderColor: tag.color, color: "#fff" }
                          : undefined
                      }
                      onClick={() => toggleTag(tag.tag)}
                    >
                      {tag.label}
                    </Badge>
                  );
                })}
              </div>
            </div>
          )}

          {/* Visible To */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5" />
              Visible To
            </Label>
            <p className="text-xs text-muted-foreground">
              Leave empty for all users. Select roles to restrict visibility.
            </p>
            <div className="flex flex-wrap gap-2">
              {ROLE_OPTIONS.map((opt) => {
                const isSelected = visibleTo.includes(opt.value);
                return (
                  <Badge
                    key={opt.value}
                    variant={isSelected ? "default" : "outline"}
                    className="cursor-pointer select-none"
                    onClick={() => toggleVisibleTo(opt.value)}
                  >
                    {opt.label}
                  </Badge>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createContact.isPending}>
              {createContact.isPending ? "Creating..." : "Create Contact"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
